import {
  appThemeApiRef,
  configApiRef,
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  DetailedHTMLProps,
  HTMLAttributes,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { matchPath, useLocation } from 'react-router';
import {
  getAiChatWidgetDisabledRoutes,
  getAiChatWidgetRoutes,
  getAiChatWidgetScriptUrl,
  getDefaultAiChatWidgetConfig,
  isAiChatWidgetEnabled,
} from './aiChatWidgetConfig';
import { resolveAiChatWidgetContext } from './aiChatWidgetContext';
import { ensureAiChatWidgetScriptLoaded } from './aiChatWidgetScriptLoader';
import {
  AI_CHAT_NAV_OFFSET,
  usePublishAppNavWidth,
} from './aiChatWidgetNavOffset';
import { installAiChatWidgetPopupLogin } from './aiChatWidgetPopupLogin';
import {
  installAiChatWidgetSqueeze,
  reapplyAiChatWidgetSqueeze,
} from './aiChatWidgetSqueeze';

// i3-chat-widget is a custom element loaded at runtime via ensureAiChatWidgetScriptLoaded.
type I3ChatWidgetElement = HTMLElement & {
  setContext: (context: string | null) => void;
  _setViewMode?: (mode: string) => void;
};

// The widget is third-party code; a throw from any of its methods must never crash the portal.
function guardWidgetCall(action: () => void): void {
  try {
    action();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('AiChatWidget: ignoring error from widget call', error);
  }
}

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'i3-chat-widget': DetailedHTMLProps<
        HTMLAttributes<HTMLElement> & {
          agent?: string;
          product?: string;
          title?: string;
          endpoint?: string;
          prompt?: string;
          model?: string;
          position?: string;
          theme?: string;
          'auto-hide'?: boolean;
          variant?: string;
          welcome?: string;
          examples?: string;
          'rating-enabled'?: boolean;
          'logo-url'?: string;
          sidebar?: string;
        },
        I3ChatWidgetElement
      >;
    }
  }
}

export const AiChatWidget = () => {
  const appThemeApi = useApi(appThemeApiRef);
  const configApi = useApi(configApiRef);
  const [themeId, setThemeId] = useState(appThemeApi.getActiveThemeId());
  const location = useLocation();
  const widgetRef = useRef<I3ChatWidgetElement>(null);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  useEffect(() => {
    const sub = appThemeApi.activeThemeId$().subscribe(setThemeId);
    return () => sub.unsubscribe();
  }, [appThemeApi]);

  const theme = themeId?.includes('dark') ? 'dark' : 'light';

  const routes = useMemo(() => getAiChatWidgetRoutes(configApi), [configApi]);
  const defaultConfig = useMemo(
    () => getDefaultAiChatWidgetConfig(configApi),
    [configApi],
  );

  const match = routes.find(route =>
    matchPath({ path: route.path, end: false }, location.pathname),
  );

  const disabledRoutes = useMemo(
    () => getAiChatWidgetDisabledRoutes(configApi),
    [configApi],
  );
  const disabledHere = disabledRoutes.some(path =>
    matchPath({ path, end: false }, location.pathname),
  );

  const enabled = isAiChatWidgetEnabled(configApi) && !disabledHere;

  // Loaded here (rather than a static <script> tag) so app.aiChatWidget.scriptUrl is read from
  // the runtime-injected config, and a config change takes effect without a frontend rebuild.
  useEffect(() => {
    if (!enabled) {
      return;
    }
    const scriptUrl = getAiChatWidgetScriptUrl(configApi);
    if (scriptUrl) {
      ensureAiChatWidgetScriptLoaded(scriptUrl);
    }
  }, [enabled, configApi]);

  // The element only gains its methods once the external widget script has run, which may be late or never.
  const [widgetDefined, setWidgetDefined] = useState(
    () => !!window.customElements.get('i3-chat-widget'),
  );
  useEffect(() => {
    if (widgetDefined) {
      return undefined;
    }
    let cancelled = false;
    void window.customElements.whenDefined('i3-chat-widget').then(() => {
      if (!cancelled) {
        setWidgetDefined(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [widgetDefined]);

  const {
    agent,
    product,
    title,
    endpoint,
    prompt,
    model,
    variant = 'default',
    welcome,
    examples,
    ratingEnabled,
    logoUrl,
    position = 'bottom-right',
    sidebar,
    autoHide,
    additionalContext,
  } = match?.config ?? defaultConfig;

  // The widget instance persists across route changes, so its hidden context is rebuilt on every
  // navigation. The previous page's context is dropped immediately, before the new one resolves.
  useEffect(() => {
    const widget = widgetRef.current;
    if (
      !enabled ||
      !widgetDefined ||
      typeof widget?.setContext !== 'function'
    ) {
      return undefined;
    }
    const controller = new AbortController();
    guardWidgetCall(() => widget.setContext(`Page: ${location.pathname}`));
    void resolveAiChatWidgetContext({
      pathname: location.pathname,
      discoveryApi,
      fetchApi,
      additionalContext,
      signal: controller.signal,
    })
      .then(context => {
        if (!controller.signal.aborted) {
          guardWidgetCall(() => widgetRef.current?.setContext?.(context));
        }
      })
      .catch(error => {
        // eslint-disable-next-line no-console
        console.error('AiChatWidget: failed to resolve page context', error);
      });
    return () => controller.abort();
  }, [
    enabled,
    widgetDefined,
    location.pathname,
    discoveryApi,
    fetchApi,
    additionalContext,
  ]);

  // The widget signs in by navigating the whole portal away; run that flow in a popup instead.
  useEffect(() => {
    const widget = widgetRef.current;
    if (!enabled || !widgetDefined || !widget) {
      return undefined;
    }
    let cleanup: (() => void) | undefined;
    guardWidgetCall(() => {
      cleanup = installAiChatWidgetPopupLogin(
        widget,
        configApi.getString('app.baseUrl'),
      );
    });
    return () => cleanup?.();
  }, [enabled, widgetDefined, configApi]);

  // In 'sidebar' view mode the panel docks to a viewport edge, so inset the app root to match.
  useEffect(() => {
    const widget = widgetRef.current;
    if (!enabled || !widget) {
      return undefined;
    }
    let cleanup: (() => void) | undefined;
    guardWidgetCall(() => {
      cleanup = installAiChatWidgetSqueeze(widget);
    });
    return () => cleanup?.();
  }, [enabled]);

  useEffect(() => {
    if (enabled) {
      reapplyAiChatWidgetSqueeze();
    }
  }, [enabled, location.pathname]);

  // Fullscreen takes over the page, so leaving the page should hand it back. Sidebar mode is
  // deliberately left alone: it is meant to persist across navigation.
  const lastPathname = useRef(location.pathname);
  useEffect(() => {
    if (lastPathname.current === location.pathname) {
      return;
    }
    lastPathname.current = location.pathname;

    const widget = widgetRef.current;
    if (widget?.getAttribute('data-i3-view-mode') === 'fullscreen') {
      guardWidgetCall(() => widget._setViewMode?.('floating'));
    }
  }, [location.pathname]);

  usePublishAppNavWidth(location.pathname);

  if (!enabled) {
    return null;
  }

  return (
    <i3-chat-widget
      ref={widgetRef}
      agent={agent}
      product={product}
      title={title}
      endpoint={endpoint}
      prompt={prompt}
      model={model}
      position={position}
      theme={theme}
      variant={variant}
      welcome={welcome}
      examples={examples?.length ? examples.join(',') : undefined}
      rating-enabled={ratingEnabled ? true : undefined}
      logo-url={logoUrl}
      sidebar={sidebar ?? AI_CHAT_NAV_OFFSET}
      auto-hide={autoHide ? true : undefined}
    />
  );
};
